import { StorageError } from '../../app/errors';
import type { FlagResolutionRow, ObservationRow } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';
import { listObservationsForMatch } from './observations';

/**
 * `flagResolutions` — DIST-1, added to the contract by requirements revision 2 (F-2).
 *
 * *"Resolution is recorded as an append-only `FlagResolution` row — never as a mutation of the flag
 * `Observation`, which INV-1 forbids — so a resolved flag MUST NOT reappear in the queue."*
 *
 * Three mechanisms carry that sentence:
 *
 * 1. **This module exports one mutator and one cascade deletion.** There is no update and no
 *    user-reachable delete, so the table is append-only in the same structural sense as
 *    `observations` — by there being nothing else to call.
 * 2. **`&observationId` is unique.** A second resolution for the same flag aborts the transaction
 *    rather than producing two, which is what makes "a resolved flag MUST NOT reappear" enforceable
 *    rather than hoped for (§3.4).
 * 3. **`listUnresolvedFlags` derives the queue** by subtracting resolutions from flags, so "resolved"
 *    is never a second piece of state that could disagree with the resolution rows.
 *
 * `matchId` is deliberately **not** on the row: it is reached through the flag Observation (§3.1). A
 * duplicate could disagree with the original, and the cascade already reaches resolutions through
 * the match's observations.
 */

/**
 * Records one triage resolution.
 *
 * `add`, not `put`: the unique `&observationId` index turns a double-resolve into an aborted
 * transaction, and `put` would quietly overwrite the first resolution instead — losing the record
 * that a flag was once promoted, on an append-only table.
 *
 * @throws {StorageError} outside a transaction, on a second resolution for the same flag, or on a
 *   write failure.
 */
export async function insertResolution(row: FlagResolutionRow): Promise<void> {
  requireTransaction('insertResolution');
  try {
    await db().flagResolutions.add(row);
  } catch (cause) {
    throw new StorageError(
      `Could not record the flag resolution. If this flag was already resolved, it must not be resolved twice (DIST-1).`,
      { cause, context: { store: TABLE.flagResolutions, id: row.id, observationId: row.observationId } },
    );
  }
}

/** INV-8's deletion path. Named for its single caller. @throws {StorageError} */
export async function deleteResolutionsForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteResolutionsForCascade');
  if (ids.length === 0) return;
  try {
    await db().flagResolutions.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the flag resolutions during a cascade.', {
      cause,
      context: { store: TABLE.flagResolutions, count: ids.length },
    });
  }
}

/** @throws {StorageError} on a read failure. */
export async function getResolutionForObservation(
  observationId: string,
): Promise<FlagResolutionRow | undefined> {
  try {
    return await db().flagResolutions.where('observationId').equals(observationId).first();
  } catch (cause) {
    throw new StorageError('Could not read the flag resolution.', {
      cause,
      context: { store: TABLE.flagResolutions, observationId },
    });
  }
}

/**
 * Every resolution belonging to a match, reached through that match's flag Observations.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listResolutionsForMatch(matchId: string): Promise<FlagResolutionRow[]> {
  // `listObservationsForMatch` is called directly, and the filter is inline, for the reason spelled
  // out on `listUnresolvedFlags`: one native `async` frame between the two Dexie queries is the
  // limit, and a `listFlags` helper spent it.
  const all = await listObservationsForMatch(matchId);
  const flagIds = all.filter((row) => row.kind === 'flag').map((row) => row.id);
  if (flagIds.length === 0) return [];
  return resolutionsForFlags(flagIds, matchId);
}

/**
 * DIST-1's triage queue: flags with no resolution row.
 *
 * Ordered `(matchMinute asc, createdAt asc)` per §10.1. `createdAt` as a tiebreak is one of INV-7's
 * three sanctioned readers — halftime-created flags all carry `matchMinute = halfLengthMin` (SES-10),
 * so without it their relative order would be arbitrary.
 *
 * ── FLAT ON PURPOSE: two Dexie queries, one `async` frame ───────────────────
 *
 * **Do not extract the flag filter into a helper, and do not route the second read through
 * `listResolutionsForMatch`.** Both were true here until U-13c, and both were defects:
 *
 * > Inside a Dexie transaction, a second query issued **after an extra native-`async` frame** loses
 * > the transaction zone. Dexie commits the `readonly` transaction at that tick boundary and the
 * > second query lands on a dead handle — `PrematureCommitError`, raised from a function whose only
 * > job is to answer a question.
 *
 * Measured, not reasoned: a probe over three shapes at one instant showed *one query at any depth*
 * → fine; *two queries, one frame* → fine; *two queries, two frames* → **fails every time**.
 *
 * It survived until now because of **who** was calling it. `bus.ts` runs it inside a `Transition`
 * snapshot surrounded by other awaits that keep the transaction busy, and every existing test
 * reached it with **zero flags**, where the early return fires before the second read ever happens.
 * A plain `read([observations, flagResolutions], …)` — which is exactly what a flags surface does —
 * failed on the first flag. *"It works"* was a property of the caller, not of this function.
 *
 * The redundant second scan of the observation ledger went with it: this used to derive the flag
 * list, then call `listResolutionsForMatch`, which derived the same list again.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listUnresolvedFlags(matchId: string): Promise<ObservationRow[]> {
  const all = await listObservationsForMatch(matchId);
  const flags = all.filter((row) => row.kind === 'flag');
  if (flags.length === 0) return [];
  const resolutions = await resolutionsForFlags(
    flags.map((row) => row.id),
    matchId,
  );
  const resolved = new Set(resolutions.map((row) => row.observationId));
  return flags
    .filter((flag) => !resolved.has(flag.id))
    .sort((a, b) => a.matchMinute - b.matchMinute || a.createdAt - b.createdAt);
}

/**
 * The resolutions belonging to a known set of flag ids.
 *
 * **Not `async`, deliberately.** It returns the Dexie promise itself (a `.catch` on one is still a
 * Dexie promise), so it adds no native frame to either caller's chain — see the note above for why
 * one frame is the budget.
 */
function resolutionsForFlags(
  flagIds: readonly string[],
  matchId: string,
): Promise<FlagResolutionRow[]> {
  return db()
    .flagResolutions.where('observationId')
    .anyOf([...flagIds])
    .toArray()
    .catch((cause: unknown) => {
      throw new StorageError('Could not read the match’s flag resolutions.', {
        cause,
        context: { store: TABLE.flagResolutions, matchId },
      });
    });
}
